import React, { useState, useEffect } from "react";

const AgeCalculator = () => {
  const [birthYear, setBirthYear] = useState(1990);
  const [birthMonth, setBirthMonth] = useState(1);
  const [birthDay, setBirthDay] = useState(1);
  const [targetYear, setTargetYear] = useState(2024);
  const [targetMonth, setTargetMonth] = useState(11);
  const [targetDay, setTargetDay] = useState(3);
  const [ageType, setAgeType] = useState("exact");
  const [roundUp, setRoundUp] = useState(false);
  const [displayFormat, setDisplayFormat] = useState("detailed");
  const [result, setResult] = useState({
    years: "0",
    months: "0",
    days: "0",
    totalDays: "0",
    totalWeeks: "0",
    totalMonths: "0",
    nextBirthday: "0",
    ageTypeText: "",
    ageTip: "",
  });

  // Age Calculation Function
  const calculateAge = () => {
    let bYear = parseInt(birthYear);
    let bMonth = parseInt(birthMonth);
    let bDay = parseInt(birthDay);
    let tYear = parseInt(targetYear);
    let tMonth = parseInt(targetMonth);
    let tDay = parseInt(targetDay);

    if (isNaN(bYear) || isNaN(bMonth) || isNaN(bDay) || isNaN(tYear) || isNaN(tMonth) || isNaN(tDay)) return;

    // Create date objects
    const birthDate = new Date(bYear, bMonth - 1, bDay);
    const targetDate = new Date(tYear, tMonth - 1, tDay);

    // Calculate age
    let years = tYear - bYear;
    let months = tMonth - bMonth;
    let days = tDay - bDay;

    // Adjust for negative days
    if (days < 0) {
      months--;
      const prevMonth = new Date(tYear, tMonth - 1, 0);
      days += prevMonth.getDate();
    }

    // Adjust for negative months
    if (months < 0) {
      years--;
      months += 12;
    }

    // Round up if selected
    if (roundUp && days > 15) {
      months++;
      days = 0;
      if (months >= 12) {
        years++;
        months = 0;
      }
    }

    // Calculate total values
    const timeDiff = targetDate - birthDate;
    const totalDays = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
    const totalWeeks = Math.floor(totalDays / 7);
    const totalMonths = years * 12 + months;

    // Calculate next birthday
    let nextBirthday = new Date(tYear, bMonth - 1, bDay);
    if (nextBirthday < targetDate) {
      nextBirthday = new Date(tYear + 1, bMonth - 1, bDay);
    }
    const daysToNextBirthday = Math.floor((nextBirthday - targetDate) / (1000 * 60 * 60 * 24));

    let typeText = "";
    let ageTip = "";

    switch(ageType) {
      case "exact":
        typeText = "Exact Age Calculation";
        ageTip = "Exact age shows years, months, and days. Perfect for official documents and precise age tracking.";
        break;
      case "simple":
        typeText = "Simple Age (Years Only)";
        ageTip = "Simple age shows completed years. Commonly used for general conversations and basic requirements.";
        break;
      case "detailed":
        typeText = "Detailed Age Breakdown";
        ageTip = "Detailed breakdown includes total days, weeks, and months. Great for milestones and anniversaries.";
        break;
      case "future":
        typeText = "Future Age Calculation";
        ageTip = "Calculate how old you'll be at a future date. Useful for planning milestones and life events.";
        break;
      case "custom":
        typeText = "Custom Date Range";
        ageTip = "Custom calculation between any two dates. Perfect for project timelines and date differences.";
        break;
      default:
        typeText = "Age Calculation";
        ageTip = "Age calculation helps track time between dates accurately.";
    }

    setResult({
      years: years.toString(),
      months: months.toString(),
      days: days.toString(),
      totalDays: totalDays.toLocaleString("en-IN"),
      totalWeeks: totalWeeks.toLocaleString("en-IN"),
      totalMonths: totalMonths.toLocaleString("en-IN"),
      nextBirthday: daysToNextBirthday.toString(),
      ageTypeText: typeText,
      ageTip,
    });
  };

 useEffect(() => {
  calculateAge();
}, [birthYear, birthMonth, birthDay, targetYear, targetMonth, targetDay, ageType, roundUp]);

  return (
    <div className="calculator-wrapper">
      <div className="calculator-container">
        {/* Header */}
        <div className="calculator-header">
          <div className="header-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="16" y1="2" x2="16" y2="6"></line>
              <line x1="8" y1="2" x2="8" y2="6"></line>
              <line x1="3" y1="10" x2="21" y2="10"></line>
            </svg>
          </div>
          <h1>Age Calculator</h1>
          <p>
            Calculate exact age, days lived, next birthday, and time between any two dates accurately
          </p>
        </div>

        {/* Main Calculator Card */}
        <div className="calculator-card">
          <div className="calculator-grid">
            {/* Left Side - Input Section */}
            <div className="input-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
                Date Details
              </h2>

              {/* Birth Year */}
              <div className="input-group">
                <label>Birth Year</label>
                <div className="input-wrapper">
                  <span className="input-prefix">📅</span>
                  <input
                    type="number"
                    value={birthYear}
                    onChange={(e) => setBirthYear(e.target.value)}
                    className="calc-input"
                    placeholder="1990"
                    min="1900"
                    max="2024"
                  />
                </div>
                <div className="quick-select-group">
                  {[1980, 1990, 2000, 2010].map((year) => (
                    <button
                      key={year}
                      onClick={() => setBirthYear(year)}
                      className="quick-select-btn primary"
                    >
                      {year}
                    </button>
                  ))}
                </div>
              </div>

              {/* Birth Month */}
              <div className="input-group">
                <label>Birth Month</label>
                <div className="input-wrapper">
                  <span className="input-prefix">📆</span>
                  <input
                    type="number"
                    value={birthMonth}
                    onChange={(e) => setBirthMonth(e.target.value)}
                    className="calc-input"
                    placeholder="1"
                    min="1"
                    max="12"
                  />
                </div>
                <div className="quick-select-group">
                  {[1, 3, 6, 12].map((month) => (
                    <button
                      key={month}
                      onClick={() => setBirthMonth(month)}
                      className="quick-select-btn success"
                    >
                      {["Jan", "Mar", "Jun", "Dec"][month === 1 ? 0 : month === 3 ? 1 : month === 6 ? 2 : 3]}
                    </button>
                  ))}
                </div>
              </div>

              {/* Birth Day */}
              <div className="input-group">
                <label>Birth Day</label>
                <div className="input-wrapper">
                  <span className="input-prefix">📍</span>
                  <input
                    type="number"
                    value={birthDay}
                    onChange={(e) => setBirthDay(e.target.value)}
                    className="calc-input"
                    placeholder="1"
                    min="1"
                    max="31"
                  />
                </div>
                <div className="quick-select-group">
                  {[1, 10, 15, 25].map((day) => (
                    <button
                      key={day}
                      onClick={() => setBirthDay(day)}
                      className="quick-select-btn success"
                    >
                      {day}
                    </button>
                  ))}
                </div>
              </div>

              {/* Age Type */}
              <div className="input-group">
                <label>Calculation Type</label>
                <div className="quick-select-group">
                  {[
                    { value: "exact", label: "Exact Age" },
                    { value: "simple", label: "Simple (Years)" },
                    { value: "detailed", label: "Detailed" }
                  ].map((type) => (
                    <button
                      key={type.value}
                      onClick={() => setAgeType(type.value)}
                      className={`quick-select-btn ${ageType === type.value ? 'primary' : 'secondary'}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
                <div className="quick-select-group" style={{marginTop: '0.5rem'}}>
                  {[
                    { value: "future", label: "Future Age" },
                    { value: "custom", label: "Custom Range" }
                  ].map((type) => (
                    <button
                      key={type.value}
                      onClick={() => setAgeType(type.value)}
                      className={`quick-select-btn ${ageType === type.value ? 'primary' : 'secondary'}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Target Year */}
              <div className="input-group">
                <label>Target Year (Calculate Age On)</label>
                <div className="input-wrapper">
                  <span className="input-prefix">🎯</span>
                  <input
                    type="number"
                    value={targetYear}
                    onChange={(e) => setTargetYear(e.target.value)}
                    className="calc-input"
                    placeholder="2024"
                    min="1900"
                    max="2100"
                  />
                </div>
                <div className="quick-select-group">
                  {[2023, 2024, 2025, 2030].map((year) => (
                    <button
                      key={year}
                      onClick={() => setTargetYear(year)}
                      className="quick-select-btn success"
                    >
                      {year}
                    </button>
                  ))}
                </div>
              </div>

              {/* Target Month */}
              <div className="input-group">
                <label>Target Month</label>
                <div className="input-wrapper">
                  <span className="input-prefix">📆</span>
                  <input
                    type="number"
                    value={targetMonth}
                    onChange={(e) => setTargetMonth(e.target.value)}
                    className="calc-input"
                    placeholder="11"
                    min="1"
                    max="12"
                  />
                </div>
                <div className="quick-select-group">
                  {[1, 6, 11, 12].map((month) => (
                    <button
                      key={month}
                      onClick={() => setTargetMonth(month)}
                      className="quick-select-btn success"
                    >
                      {["Jan", "Jun", "Nov", "Dec"][month === 1 ? 0 : month === 6 ? 1 : month === 11 ? 2 : 3]}
                    </button>
                  ))}
                </div>
              </div>

              {/* Target Day */}
              <div className="input-group">
                <label>Target Day</label>
                <div className="input-wrapper">
                  <span className="input-prefix">📍</span>
                  <input
                    type="number"
                    value={targetDay}
                    onChange={(e) => setTargetDay(e.target.value)}
                    className="calc-input"
                    placeholder="3"
                    min="1"
                    max="31"
                  />
                </div>
                <div className="quick-select-group">
                  {[1, 15, 25, 30].map((day) => (
                    <button
                      key={day}
                      onClick={() => setTargetDay(day)}
                      className="quick-select-btn success"
                    >
                      {day}
                    </button>
                  ))}
                </div>
              </div>

              {/* Round Up */}
              <div className="input-group">
                <label>Round Months?</label>
                <div className="quick-select-group">
                  {[
                    { value: true, label: "Yes (Round Up)" },
                    { value: false, label: "No (Exact)" }
                  ].map((type) => (
                    <button
                      key={type.value.toString()}
                      onClick={() => setRoundUp(type.value)}
                      className={`quick-select-btn ${roundUp === type.value ? 'success' : 'secondary'}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              <button onClick={calculateAge} className="calculate-btn">
                Calculate Age
              </button>
            </div>

            {/* Right Side - Results Section */}
            <div className="result-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <circle cx="12" cy="12" r="6"></circle>
                  <circle cx="12" cy="12" r="2"></circle>
                </svg>
                Age Calculation
              </h2>

              <div className="result-card">
                {/* Current Age - Highlighted */}
                <div className="result-item highlight">
                  <div className="result-item-header">
                    <span className="result-label">Current Age</span>
                    <div className="result-icon-wrapper white">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                        <circle cx="12" cy="7" r="4"></circle>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    {result.years} Years
                  </div>
                  <div className="result-subtext">
                    {result.months} Months, {result.days} Days
                  </div>
                </div>

                {/* Total Days */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Total Days Lived</span>
                    <div className="result-icon-wrapper green">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#10b981'}}>
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    {result.totalDays}
                  </div>
                  <div className="result-subtext">
                    Days since birth
                  </div>
                </div>

                {/* Total Weeks */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Total Weeks</span>
                    <div className="result-icon-wrapper blue">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#3b82f6'}}>
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                        <line x1="16" y1="2" x2="16" y2="6"></line>
                        <line x1="8" y1="2" x2="8" y2="6"></line>
                        <line x1="3" y1="10" x2="21" y2="10"></line>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    {result.totalWeeks}
                  </div>
                  <div className="result-subtext">
                    Weeks since birth
                  </div>
                </div>

                {/* Total Months */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Total Months</span>
                    <div className="result-icon-wrapper purple">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#9333ea'}}>
                        <circle cx="12" cy="12" r="10"></circle>
                        <polyline points="12 6 12 12 16 14"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    {result.totalMonths}
                  </div>
                  <div className="result-subtext">
                    Months since birth
                  </div>
                </div>

                {/* Next Birthday */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Next Birthday In</span>
                    <div className="result-icon-wrapper green">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#10b981'}}>
                        <path d="M20 12v6a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-6"></path>
                        <path d="M12 3v9"></path>
                        <path d="m8 8 4-4 4 4"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    {result.nextBirthday} Days
                  </div>
                  <div className="result-subtext">
                    Until your next birthday
                  </div>
                </div>

                {/* Visual Breakdown */}
                <div className="breakdown-section">
                  <h3 className="breakdown-title">Age Breakdown</h3>
                  
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Years</span>
                      <span className="breakdown-percentage">
                        {result.years} Years
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill blue"
                        style={{
                          width: `${parseInt(result.totalMonths) > 0 ? ((parseInt(result.years) * 12) / parseInt(result.totalMonths)) * 100 : 0}%`,
                        }}
                      />
                    </div>
                  </div>

                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Remaining Months</span>
                      <span className="breakdown-percentage">
                        {result.months} Months
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill green"
                        style={{
                          width: `${parseInt(result.totalMonths) > 0 ? (parseInt(result.months) / parseInt(result.totalMonths)) * 100 : 0}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Time Lived Distribution */}
                <div className="breakdown-section" style={{marginTop: '1.5rem'}}>
                  <h3 className="breakdown-title">Time Lived Distribution</h3>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">In Weeks</span>
                      <span className="breakdown-percentage">
                        {result.totalWeeks} Weeks
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill blue"
                        style={{
                          width: `${parseInt(result.totalDays) > 0 ? (parseInt(result.totalWeeks.replace(/,/g, '')) * 7 / parseInt(result.totalDays.replace(/,/g, ''))) * 100 : 0}%`,
                        }}
                      />
                    </div>
                  </div>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">In Days</span>
                      <span className="breakdown-percentage">
                        {result.totalDays} Days
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill green"
                        style={{
                          width: `100%`,
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Age Tip Box */}
                <div className="breakdown-section" style={{marginTop: '1.5rem', padding: '1rem', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #10b981'}}>
                  <h3 className="breakdown-title" style={{color: '#065f46', marginBottom: '0.5rem'}}>Age Insights</h3>
                  <p style={{fontSize: '0.875rem', color: '#065f46', lineHeight: '1.5', margin: 0}}>
                    {result.ageTip} Every day is a milestone worth celebrating!
                  </p>
                </div>
              </div>
            </div>
          </div>
          
        </div>

        {/* Info Section */}
        <div className="info-section">
          <div className="info-header">
            <div className="info-icon-wrapper">
              <svg className="info-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
            </div>
            <div>
              <h3>Understanding Age Calculation</h3>
              <p>
                Age calculation determines the time elapsed between birth date and a reference date (usually today). 
                It's expressed in years, months, and days. Accurate age calculation is important for legal documents, 
                education enrollment, retirement planning, and medical records. In most countries, age increases on 
                your birthday regardless of the exact time you were born. Leap years affect age calculations - people 
                born on February 29 celebrate birthdays on February 28 or March 1 in non-leap years. Age can be 
                calculated in different units: chronological age (time since birth), biological age (physical condition), 
                and mental age (cognitive development). For official purposes, completed years are counted - you're 
                considered a certain age only after completing that many years. Age calculation helps determine 
                eligibility for voting (18+), driving licenses (varies by country), senior citizen benefits (60+), and 
                retirement (usually 60-65). Understanding your exact age in days or weeks can be fascinating - the 
                average person lives about 27,000-29,000 days. Age calculators are useful for planning milestone 
                celebrations, calculating work experience, and determining insurance premiums.
              </p>
            </div>
          </div>

          <div className="formula-section">
            <h4>Age Calculation Method:</h4>
            <div className="formula-display">
              Age = Target Date - Birth Date (in Years, Months, Days)
            </div>

            <div className="formula-params">
              <div className="formula-param">
                <div className="formula-param-key">Years</div>
                <div className="formula-param-value">Target Year - Birth Year (adjusted for month/day)</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Total Days</div>
                <div className="formula-param-value">Difference in milliseconds / (1000 × 60 × 60 × 24)</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Total Weeks</div>
                <div className="formula-param-value">Total Days / 7</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Total Months</div>
                <div className="formula-param-value">(Years × 12) + Remaining Months</div>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="faq-section">
            <h3>Frequently Asked Questions</h3>

            <div className="faq-item">
              <h5>How is age calculated exactly?</h5>
              <p>
                Age is calculated by finding the difference between birth date and target date. Years are counted first, then remaining months, then remaining days. Adjustments are made for varying month lengths.
              </p>
            </div>

            <div className="faq-item">
              <h5>What if I was born on February 29 (leap day)?</h5>
              <p>
                People born on February 29 celebrate birthdays on February 28 or March 1 in non-leap years. Legally, age increases on February 28 at 11:59 PM or March 1 at 12:00 AM depending on jurisdiction.
              </p>
            </div>

            <div className="faq-item">
              <h5>When am I considered a certain age legally?</h5>
              <p>
                Legally, you attain a certain age at the start of your birthday (12:00 AM). Some countries use "completed years" - you're 18 only after completing 18 full years from birth.
              </p>
            </div>

            <div className="faq-item">
              <h5>How do I calculate age for official documents?</h5>
              <p>
                For official documents, use completed years only. Count from birth date to document date. If birthday hasn't occurred in the current year, subtract one from the year difference.
              </p>
            </div>

            <div className="faq-item">
              <h5>What's the difference between chronological and biological age?</h5>
              <p>
                Chronological age is time since birth (calculated age). Biological age reflects physical health and can differ based on lifestyle, genetics, and health habits. A 50-year-old might have a biological age of 40.
              </p>
            </div>

            <div className="faq-item">
              <h5>How many days does an average person live?</h5>
              <p>
                If someone lives 75 years, they've lived approximately 27,375 days or 3,900 weeks. At 80 years, that's about 29,200 days. Every day is precious!
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
    
  );
};

export default AgeCalculator;